// Hashing — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as arrays.js, with the same depth:
// concept → internals → operations → the four sub-patterns → errors → complexity →
// beginner drills → cheatsheet → interactive playground.

import hashingDrills from './hashingDrills';
const drill = id => hashingDrills.find(d => d.id === id);

const hashing = {
  key: 'Hashing',
  tagline: 'Trade a little memory for a lot of speed — turn "search everything" into "go straight there."',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Hashing',
      type: 'text',
      body: [
        "Scanning an array to check \"does 7 exist?\" costs O(n) — you check every element until you find it or run out. Hashing removes that scan entirely.",
        "A HashMap<K,V> stores key → value. A HashSet<E> stores just keys — think of it as a HashMap that only cares about \"have I seen this?\" A hash function turns each key into a bucket address, so lookup, insert, and delete all become O(1) on average, no matter how large the collection grows.",
      ],
    },
    {
      id: 'why',
      title: 'Why Hashing?',
      type: 'text',
      body: [
        'The moment you catch yourself writing a nested loop purely to compare every element against every other element, that O(n²) scan is almost always replaceable with one pass plus a hash map, dropping it to O(n).',
        'You are trading space for time: a HashMap can cost O(n) extra memory to save you from O(n) or O(n²) extra time. That trade is the entire pattern.',
        'Four sub-patterns cover almost everything you\'ll meet: frequency counting, seen-before detection, complement lookup (Two Sum-style), and grouping by a derived signature (anagrams).',
      ],
    },
    {
      id: 'analogy',
      title: 'How Hashing Works — The PNR Analogy',
      type: 'text',
      heading: 'The PNR Counter, Not the Passenger Register',
      body: [
        "When you check an Indian Railways ticket, you never get handed a paper register of every passenger on the train to scan row by row for your name. You type your 10-digit PNR, and the system computes exactly where that record lives and jumps straight to it — no scanning, no matter how many thousand passengers are on the list. That's a hash map: your PNR is the key, Java's hashCode() computes the \"address,\" and the JVM goes directly to that bucket.",
        "Now stretch the analogy to Two Sum: instead of checking every passenger against every other passenger to find a pair whose berth counts add up to a target (that's the brute-force O(n²)), you keep a running register of passengers you've already seen, indexed by their count. When a new passenger arrives, you instantly check the register: \"is there already someone here whose count plus mine hits the target?\" One pass, O(n) — the register (HashMap) is doing the work an array full scan would otherwise repeat for every single passenger.",
      ],
    },
    {
      id: 'internals',
      title: 'How a HashMap Works Internally',
      type: 'bucket-diagram',
      body: [
        "Under the hood, a HashMap is really just an array of \"buckets.\" Every key's hashCode() gets reduced to a bucket index — typically hashCode() % capacity — and the key/value pair is stored in that bucket.",
        "Two different keys can land in the same bucket (a collision). Java handles this by chaining: each bucket holds a small list of entries, and Java checks each one with .equals() until it finds the right key. If a single bucket gets too crowded (8+ entries since Java 8), that bucket upgrades from a linked list to a red-black tree, capping worst-case lookup at O(log n) instead of O(n).",
        "This is also why a good hashCode() matters: a poor one crams everything into a few buckets, turning your \"O(1) average\" map into a slow linked list in disguise.",
      ],
      demo: {
        capacity: 5,
        entries: [
          { key: 'date', hash: 0 },
          { key: 'apple', hash: 2 },
          { key: 'banana', hash: 3 },
          { key: 'cherry', hash: 3 },
        ],
        note: 'bucket index = hashCode() % 5 — "banana" and "cherry" collide into bucket 3 and get chained together.',
      },
    },
    {
      id: 'declare',
      title: 'Declaring & Creating a HashMap / HashSet',
      type: 'code',
      code:
`Map<String, Integer> map = new HashMap<>();
Set<String> set = new HashSet<>();

// with initial values already known
Map<Character, Integer> freq = new HashMap<>(Map.of('a', 1, 'b', 2));`,
      note: 'HashMap and HashSet are the workhorses — no ordering guarantee, but the fastest average-case lookup Java offers.',
    },
    {
      id: 'operations',
      title: 'Core Operations: put, get, containsKey, remove',
      type: 'code',
      code:
`Map<String, Integer> scores = new HashMap<>();

scores.put("Asha", 88);          // insert
scores.put("Asha", 91);          // overwrites silently — same key, no warning

int marks = scores.get("Asha");            // 91
boolean has = scores.containsKey("Ravi");   // false

scores.remove("Asha");           // deletes the entry entirely`,
      note: 'put() always overwrites silently if the key already exists — there\'s no exception or warning, so double-check that\'s actually what you want.',
    },
    {
      id: 'iterate',
      title: 'Iterating a HashMap',
      type: 'code',
      code:
`for (Map.Entry<String, Integer> entry : scores.entrySet()) {
    System.out.println(entry.getKey() + " -> " + entry.getValue());
}

for (String key : scores.keySet())   { /* key only */ }
for (Integer value : scores.values()) { /* value only */ }`,
      note: 'entrySet() gets both key and value together in one pass. Looping keySet() and calling get(key) inside the loop works, but does a second lookup per key for no reason.',
    },
    {
      id: 'freq',
      title: 'Frequency Counting',
      type: 'code',
      code:
`Map<Character, Integer> freq = new HashMap<>();
for (char c : s.toCharArray()) {
    freq.put(c, freq.getOrDefault(c, 0) + 1);
}`,
      note: 'getOrDefault avoids a manual containsKey check — the classic Java idiom for tally maps.',
    },
    {
      id: 'seen',
      title: 'Duplicate / Seen-Before Detection',
      type: 'code',
      code:
`Set<Integer> seen = new HashSet<>();
for (int num : nums) {
    if (!seen.add(num)) {
        return true; // add() returns false if the value was already present
    }
}
return false;`,
      note: 'Set.add() doubles as both the insert AND the membership check — no separate contains() call needed.',
    },
    {
      id: 'complement',
      title: 'Complement Lookup (Two Sum Pattern)',
      type: 'code',
      code:
`Map<Integer, Integer> indexOf = new HashMap<>();
for (int i = 0; i < nums.length; i++) {
    int complement = target - nums[i];
    if (indexOf.containsKey(complement)) {
        return new int[] { indexOf.get(complement), i };
    }
    indexOf.put(nums[i], i);
}`,
      note: 'One pass: check for the complement BEFORE inserting the current value, so you never match an element with itself.',
    },
    {
      id: 'group',
      title: 'Grouping by Signature (Anagrams)',
      type: 'code',
      code:
`Map<String, List<String>> groups = new HashMap<>();
for (String word : words) {
    char[] chars = word.toCharArray();
    Arrays.sort(chars);
    String signature = new String(chars);
    groups.computeIfAbsent(signature, k -> new ArrayList<>()).add(word);
}`,
      note: 'computeIfAbsent creates the bucket list only if it does not exist yet, then appends — one line instead of four.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Using a plain array (int[]) as a HashMap key',
          why: "Arrays inherit Object's default hashCode()/equals(), which compare identity, not contents — two arrays with identical elements are treated as different keys.",
          fix: 'Convert the array into a canonical String or List signature first (see the anagram-grouping pattern above), then use that as the key.',
        },
        {
          mistake: 'Using a mutable object as a key',
          why: "If a key object's fields change after insertion, its hashCode changes too, and the entry becomes unreachable in its old bucket — silently, with no exception thrown.",
          fix: 'Prefer immutable keys (String, Integer, Long) or make a defensive, unchanging copy before using an object as a key.',
        },
        {
          mistake: 'Calling get() and assuming a non-null result',
          why: "map.get(key) returns null when the key isn't present — using the result immediately (e.g. unboxing it into an int) throws a NullPointerException.",
          fix: 'Use getOrDefault(key, fallback), or explicitly check containsKey(key) first.',
        },
        {
          mistake: 'Modifying a map while iterating it directly',
          why: "Calling map.remove() or map.put() inside a for-each loop over entrySet()/keySet() throws a ConcurrentModificationException — the iterator detects the structure changed underneath it.",
          fix: 'Use an Iterator explicitly and call iterator.remove(), or collect the keys to remove first and delete them in a separate pass afterward.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'get / put / containsKey — HashMap', time: 'O(1) average', note: 'O(log n) worst case since Java 8 (heavily collided buckets treeify).' },
        { op: 'add / contains — HashSet', time: 'O(1) average', note: 'Same bucket mechanics as HashMap under the hood.' },
        { op: 'Iterating all entries', time: 'O(n)', note: 'Every bucket and every chained entry is visited once.' },
        { op: 'get / put — TreeMap (sorted)', time: 'O(log n)', note: 'Pays for ordering with a red-black tree instead of buckets.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for hashing when you see…',
      body: [
        'You need to repeatedly ask "have I seen this value before?" inside a loop.',
        'You need counts / frequencies of elements (characters, numbers, words).',
        'You need to find a complement or pair matching a target (target − x).',
        'You need to group elements by a derived property, like anagrams by sorted signature.',
        'Avoid it when you need sorted or k-th-smallest access cheaply — a TreeMap or a plain sort beats bolting order onto a HashMap.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('valid-anagram'),
            drill('first-non-repeating-char'),
            drill('count-frequency-elements'),
            drill('two-arrays-equal-ignoring-order'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('longest-consecutive-sequence'),
            drill('subarray-sum-equals-k'),
            drill('pairs-with-given-sum'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('longest-substring-no-repeat'),
            drill('isomorphic-strings'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with the four hashing sub-patterns before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'HashMap<K,V> for key→value; HashSet<E> when you only need existence.',
        'getOrDefault, putIfAbsent, computeIfAbsent, merge — the four methods that erase boilerplate.',
        'O(1) average for get/put/contains — the entire reason this pattern exists.',
        'Internally: bucket index = hashCode() % capacity. Collisions chain within a bucket; heavily collided buckets treeify to O(log n) since Java 8.',
        'Keys must be immutable and must correctly override equals()/hashCode() — String and boxed numeric types already do.',
        'No ordering guarantee from HashMap/HashSet — reach for LinkedHashMap (insertion order) or TreeMap (sorted order, O(log n)) when order matters.',
        'A nested loop comparing every element to every other element is almost always a hash-map opportunity in disguise.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default hashing;
